import { useCallback, useMemo, useRef } from 'react'
import {
  useHasPendingApproval,
  useTransactionAdder,
} from '~/state/transactions/hooks'
import { FixedPointNumber } from '~/utils/fixedPoint'
import { useTokenContract } from '.'
import { AddressZero, MaxUint256 } from '@ethersproject/constants'
import { TransactionResponse } from '@ethersproject/providers'
import { useTokenAllowance } from './useTokenAllowance'
import { TransactionType } from '~/state/transactions/actions'
import { gasSpeedup } from '~/utils/gasSpeedup'
import { BigNumber } from 'ethers'
import { useApprovalModalStateDispatch } from '~/state/application/hooks'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import { useAccount, useChainId, usePublicClient } from 'wagmi'
import { useAllowanceTarget } from '~/state/dex/hooks'
import { queryClient } from '~/components/Web3Provider'

export const useApproveCallback = (
  tokenToApprove: ERC20Token,
  amountToApprove?: BigNumber,
  spender?: string,
): [ApprovalState, () => Promise<void>] => {
  const chainId = useChainId()
  const publicClient = usePublicClient()
  const tokenContract = useTokenContract(tokenToApprove?.address)
  const timeoutRef = useRef()

  const approvalState = useApprovalState(
    tokenToApprove,
    amountToApprove,
    spender,
  )
  const addTransaction = useTransactionAdder()
  const updateApprovalState = useApprovalModalStateDispatch()

  const approve = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (!tokenContract) {
      console.error('Token contract is null')
      return
    }

    if (!chainId) {
      console.error('Chain ID is null')
      return
    }

    if (!spender) {
      console.error('Spender is not specified')
      return
    }

    if (!amountToApprove) {
      console.error('Amount to approve is not specified')
      return
    }

    updateApprovalState({
      progress: amountToApprove.isZero()
        ? ApprovalProgress.CANCEL_APPROVING
        : ApprovalProgress.APPROVING,
      hash: '',
    })

    let gasPrice = undefined
    try {
      gasPrice = await publicClient.getGasPrice()
    } catch (error) {
      console.warn('failed to get gasPrice')
    }
    console.log(
      !gasPrice ? 'not found gasPrice' : 'gasPrice ' + gasPrice.toString(),
    )

    const estimateGas = await tokenContract.estimateGas
      .approve(spender, amountToApprove)
      .catch(() => {
        return tokenContract.estimateGas.approve(spender, MaxUint256)
      })

    return tokenContract
      .approve(spender, amountToApprove.toString(), {
        gasLimit: gasSpeedup(estimateGas),
        // @notice: 这里手动加 gasPrice 是因为 imToken 2.10.3 版本，arbitrum 网络 App 内获取 gasPrice 有延迟
        // 可能会出现 gas 过低的情况，所以这边手动通过 rpc 获取 gasPrice
        gasPrice,
      })
      .then((response: TransactionResponse) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        addTransaction(response, {
          type: TransactionType.APPROVAL,
          tokenAddress: tokenToApprove?.address,
          spender,
          amountToApprove: amountToApprove.toString(),
        })
        updateApprovalState({ hash: response.hash })
        
        // Invalidate all queries after approval succeeds
        // This ensures fresh allowance, balance, and contract status data
        queryClient.invalidateQueries()
      })
      .catch((error) => {
        console.error('allowance failed', error)
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        updateApprovalState({
          progress: amountToApprove.isZero()
            ? ApprovalProgress.CANCEL_REJECTED
            : ApprovalProgress.APPROVE_REJECTED,
        })
      })
  }, [
    approvalState,
    tokenToApprove,
    amountToApprove,
    spender,
    addTransaction,
    updateApprovalState,
    chainId,
    tokenContract,
  ])

  return [approvalState, approve]
}

export enum ApprovalState {
  UNKNOWN = 'UNKNOWN',
  NOT_APPROVED = 'NOT_APPROVED',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
}

export const useApprovalState = (
  tokenToApprove: ERC20Token,
  amountToApprove?: BigNumber,
  spender?: string,
) => {
  const { address: account } = useAccount()

  const currentAllowance = useTokenAllowance(
    tokenToApprove,
    account ?? undefined,
    spender,
  )
  const pendingApproval = useHasPendingApproval(
    tokenToApprove?.address,
    spender,
  )

  return useMemo(() => {
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN
    if (tokenToApprove?.address === AddressZero) return ApprovalState.UNKNOWN

    if (!currentAllowance) return ApprovalState.UNKNOWN

    return currentAllowance.isLessThan(new FixedPointNumber(amountToApprove))
      ? pendingApproval
        ? ApprovalState.PENDING
        : ApprovalState.NOT_APPROVED
      : ApprovalState.APPROVED
  }, [amountToApprove, currentAllowance, pendingApproval, spender])
}

export const useNeedReduceAllowance2Zero = (
  tokenToApprove: ERC20Token,
  spender?: string,
) => {
  const tokenContract = useTokenContract(tokenToApprove?.address)
  const allowanceTarget = useAllowanceTarget()

  return useCallback(async () => {
    try {
      const estimateGas = await tokenContract.estimateGas.approve(
        spender ?? allowanceTarget,
        MaxUint256,
      )
      return !estimateGas
    } catch (error) {
      return true
    }
  }, [tokenToApprove, spender, allowanceTarget, tokenContract])
}
